/**
 * Pipeline board — `pipeline.html` ported.
 *
 * Rendered as the "Board" half of the Opportunities screen
 * (`opportunities/OpportunitiesPage.jsx` toggles between this and the list),
 * not its own nav item or route any more — same deals, same permission, two
 * layouts of one screen. `/pipeline` now just redirects to
 * `/opportunities?view=board` for old links and bookmarks.
 *
 * A kanban, deliberately **not paginated** beyond `limit_per_stage` — see
 * `GET /pipeline/board`'s own doc comment in `opportunities.service.js`.
 * Drag-and-drop moves a deal between stages via `POST /opportunities/{id}/stage`;
 * closing as lost opens `StageChangeModal` for the required reason instead of
 * dropping straight through, the same as a rejected drop would otherwise need
 * a second dialog anyway.
 *
 * The prototype's "Playbook" card is a fixed fake checklist with no schema
 * behind it. Replaced with the real `exit_criteria` text `md.opportunity_stage`
 * actually carries, for whichever stages have one set.
 */
import { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import {
  ChipSelect, ErrorState, Avatar, Badge,
} from '../../components/common/index.jsx';
import { PageLoader } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import { money, moneyShort, date, number } from '../../utils/formatters.js';
import {
  usePipelineBoard, usePipelineVelocity, useOpportunityOptions, useChangeStage,
} from '../opportunities/useOpportunities.js';
import { StageChangeModal } from '../opportunities/OpportunityModals.jsx';

export default function PipelineBoardView() {
  const { can, hasScope } = useAuth();
  /** `own` scope means RLS already limits the board to this user's own deals —
   * an Owner filter could only ever return what's already showing or nothing. */
  const canFilterByOwner = hasScope('pipeline', 'team');
  const options = useOpportunityOptions();
  const [pipelineId, setPipelineId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  const [stageChange, setStageChange] = useState(null);

  const effectivePipelineId = pipelineId || options.pipelines.find((p) => p.isDefault)?.value || options.pipelines[0]?.value;
  const boardParams = useMemo(
    () => ({ pipeline_id: effectivePipelineId || undefined, owner_id: ownerId || undefined }),
    [effectivePipelineId, ownerId],
  );
  const { data, isLoading, error, refetch } = usePipelineBoard(boardParams);
  const { data: velocity } = usePipelineVelocity({ pipeline_id: effectivePipelineId || undefined });
  const stageMutation = useChangeStage();

  const columns = data?.columns ?? [];
  const totals = data?.totals ?? { count: 0, amount: 0, weighted: 0 };
  const staleCards = columns.flatMap((c) => c.cards.filter((card) => card.is_stale));
  const staleAmount = staleCards.reduce((sum, c) => sum + c.amount, 0);

  const stagesWithCriteria = options.stages.filter((s) => s.pipelineId === effectivePipelineId && s.exitCriteria);

  const onDrop = (column) => {
    if (!dragging) return;
    if (column.stage.id === dragging.stageId) { setDragging(null); return; }

    const target = options.stages.find((s) => s.value === column.stage.id);
    if (target?.isClosed) {
      setStageChange({
        opportunity: { id: dragging.id, name: dragging.name, amount: dragging.amount, currency: dragging.currency, rowVersion: dragging.rowVersion },
        target,
      });
    } else {
      stageMutation.mutate({ id: dragging.id, payload: { stage_id: column.stage.id, row_version: dragging.rowVersion } });
    }
    setDragging(null);
    setDragOverStageId(null);
  };

  if (isLoading) return <PageLoader label="Loading pipeline" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <p className="page-sub mb-0">
          {data?.pipeline?.name ?? 'Pipeline'} · {number(totals.count)} open deals · drag a card to move it between stages
          {isMock(data) && ' (mock)'}
        </p>
        <select
          className="form-select form-select-sm w-auto"
          value={effectivePipelineId || ''}
          disabled={options.loading && options.pipelines.length === 0}
          onChange={(e) => setPipelineId(e.target.value)}
        >
          {options.loading && options.pipelines.length === 0
            ? <option value="">Loading…</option>
            : options.pipelines.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#eef2ff' }}>
            <div className="stat-label">Total pipeline</div>
            <div className="stat-value t-xl mt-1">{moneyShort(totals.amount)}</div>
            <div className="stat-meta mt-1">{number(totals.count)} open deals</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#f5f3ff' }}>
            <div className="stat-label">Weighted value</div>
            <div className="stat-value t-xl mt-1">{moneyShort(totals.weighted)}</div>
            <div className="stat-meta mt-1">Probability adjusted</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#ecfdf5' }}>
            <div className="stat-label">Win rate</div>
            <div className="stat-value t-xl mt-1">{velocity?.overall?.win_rate_pct ?? 0}%</div>
            <div className="stat-meta mt-1">Avg cycle {velocity?.overall?.avg_sales_cycle_days ?? '—'}d</div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat fade-up" style={{ '--tint': '#fff1f2' }}>
            <div className="stat-label">Aging &gt; 30 days</div>
            <div className="stat-value t-xl mt-1">{staleCards.length}</div>
            <div className="stat-meta mt-1"><span className="pill pill-rose">{moneyShort(staleAmount)} at risk</span></div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <ChipSelect
          label="Owner" allLabel="All owners" value={ownerId} onChange={setOwnerId} options={options.owners}
          disabled={!canFilterByOwner} disabledReason="You can only see your own pipeline, so filtering by owner has nothing else to show."
        />
      </div>

      <div className="kanban fade-up">
        {columns.map((column) => (
          <div key={column.stage.id} className="kan-col">
            <div className="stage-line" style={{ background: 'var(--brand-500)' }} />
            <div className="kan-head">
              <div className="d-flex align-items-center justify-content-between">
                <span className="name">{column.stage.name}</span>
                <span className="pill pill-gray">{column.stage.probability}%</span>
              </div>
              <div className="meta mt-1">{column.totals.count} deals · <strong className="ink-2">{moneyShort(column.totals.amount)}</strong></div>
            </div>
            <div
              className={`kan-body ${dragOverStageId === column.stage.id ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverStageId(column.stage.id); }}
              onDragLeave={() => setDragOverStageId((s) => (s === column.stage.id ? null : s))}
              onDrop={() => onDrop(column)}
            >
              {column.cards.map((card) => (
                <div
                  key={card.id}
                  className={`deal ${dragging?.id === card.id ? 'dragging' : ''}`}
                  draggable={can('opportunities', 'edit')}
                  onDragStart={() => setDragging({ ...card, stageId: column.stage.id, rowVersion: card.row_version })}
                  onDragEnd={() => { setDragging(null); setDragOverStageId(null); }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="min-w-0">
                      <div className="d-title text-truncate">{card.name}</div>
                      <div className="d-co text-truncate">{card.account}</div>
                    </div>
                    {card.is_stale && <Badge tone="rose">Stale</Badge>}
                  </div>
                  <div className="d-amt mt-2">{money(card.amount, card.currency)}</div>
                  <div className="bar thin mt-2"><span style={{ width: `${column.stage.probability}%`, background: 'var(--brand-500)' }} /></div>
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    {card.owner ? <Avatar name={card.owner.full_name} size={22} /> : <span />}
                    <span className="t-2xs ink-4"><i className="bi bi-calendar3 me-1" aria-hidden="true" />{date(card.expected_close_date)}</span>
                  </div>
                </div>
              ))}
              {column.cards.length === 0 && <p className="t-xs text-muted text-center py-4 mb-0">Nothing in this stage</p>}
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-7">
          <div className="card-x fade-up">
            <div className="card-head"><h5>Stage conversion &amp; velocity</h5><span className="ms-auto t-2xs ink-4">All time</span></div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Stage</th><th className="num">Deals in</th><th className="num">Advanced</th><th className="num">Conversion</th><th className="num">Avg days</th><th>Health</th></tr></thead>
                <tbody>
                  {(velocity?.stages ?? []).length === 0 ? (
                    <tr><td colSpan={6} className="t-sm ink-3 text-center py-3">No stage history yet.</td></tr>
                  ) : velocity.stages.map((s) => (
                    <tr key={s.stage}>
                      <td className="strong">{s.stage}</td>
                      <td className="num">{s.entered}</td>
                      <td className="num">{s.advanced}</td>
                      <td className="num">{s.conversion_pct}%</td>
                      <td className="num">{s.avg_days ?? '—'}</td>
                      <td><span className={`pill ${s.conversion_pct >= 60 ? 'pill-green' : 'pill-amber'}`}>{s.conversion_pct >= 60 ? 'Healthy' : 'Slowing'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card-x fade-up h-100">
            <div className="card-head"><h5>Exit criteria</h5></div>
            <div className="card-body-x t-sm">
              {stagesWithCriteria.length === 0 ? (
                <p className="t-sm ink-3 mb-0">No stage in this pipeline has written exit criteria yet.</p>
              ) : stagesWithCriteria.map((s) => (
                <div key={s.value} className="mb-3">
                  <div className="fw-650 t-sm mb-1">{s.label}</div>
                  <div className="t-xs ink-3">{s.exitCriteria}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <StageChangeModal
        open={Boolean(stageChange)}
        opportunity={stageChange?.opportunity}
        targetStage={stageChange?.target}
        options={options}
        busy={stageMutation.isPending}
        onClose={() => setStageChange(null)}
        onConfirm={async (payload) => {
          await stageMutation.mutateAsync({ id: stageChange.opportunity.id, payload: { ...payload, row_version: stageChange.opportunity.rowVersion } });
          setStageChange(null);
        }}
      />

    </>
  );
}
